import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import {
  getMyAttendance,
  getCheckinData,
  checkin,
  getCheckoutData,
  checkout,
} from '@/api/attendance'
import { createLeave } from '@/api/leave'

function isoLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * The current user's own check-in/check-out flow: status, the carryover/goal
 * data each modal needs, the mutations, and the small pieces of orchestration
 * around them (WFH toggle, the "leaving before EOD?" gate, the pending-leave
 * hand-off after checkout). Shared by the employee "My Attendance" page and
 * the admin HR dashboard's check-in/out card so both drive the exact same
 * behavior instead of maintaining two copies of it.
 */
export function useCheckInOut() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const [showCheckin, setShowCheckin] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)
  const [isWfhCheckin, setIsWfhCheckin] = useState(false)
  const [pendingLeaveType, setPendingLeaveType] = useState<string | null>(null)
  const [showEarlyLeavePrompt, setShowEarlyLeavePrompt] = useState(false)
  const [earlyLeaveReason, setEarlyLeaveReason] = useState('')
  const [pendingMsg, setPendingMsg] = useState('')

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['attendance', 'me'],
    queryFn: getMyAttendance,
    refetchInterval: 60_000,
  })

  const { data: checkinData } = useQuery({
    queryKey: ['attendance', 'checkin-data', showCheckin],
    queryFn: getCheckinData,
    enabled: showCheckin,
    staleTime: 0,
  })

  const { data: checkoutData } = useQuery({
    queryKey: ['attendance', 'checkout-data', showCheckout],
    queryFn: getCheckoutData,
    enabled: showCheckout,
    staleTime: 0,
  })

  const checkinMutation = useMutation({
    mutationFn: (data: { carryover_goal_ids: string[]; new_goals: string[]; wfh?: boolean; at_time?: string; reason?: string }) =>
      checkin(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
      queryClient.invalidateQueries({ queryKey: ['leave'] })
      setShowCheckin(false)
      setIsWfhCheckin(false)
      if (data?.pending_approval) setPendingMsg(data.message || 'Your request was sent for approval.')
      else if (data?.message) setPendingMsg(data.message) // e.g. WFH over the monthly limit sent for approval
    },
    onError: () => {
      setIsWfhCheckin(false)
    },
  })

  const checkoutMutation = useMutation({
    mutationFn: checkout,
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
      setShowCheckout(false)
      setEarlyLeaveReason('')
      if (data?.pending_approval) setPendingMsg(data.message || 'Your request was sent for approval.')
      else if (data?.message) setPendingMsg(data.message) // e.g. late check-out sent for approval
      // If there's a pending leave (picked while still checked in), apply it after checkout.
      if (pendingLeaveType) {
        try {
          await createLeave({ date: isoLocalDate(new Date()), type: pendingLeaveType })
          queryClient.invalidateQueries({ queryKey: ['leave'] })
        } catch {
          // leave creation failed, ignore
        }
        setPendingLeaveType(null)
      }
    },
  })

  const isCheckedIn = !!status?.checked_in && !status?.checked_out
  const isCheckedOut = !!status?.checked_out

  /** True while the user is still within their (possibly "Getting Late"
   * extended) shift window — used to gate the early-leave reason prompt. */
  const isBeforeEod = () => {
    const eod = status?.effective_eod_time || user?.eod_time || '17:00'
    const standup = status?.effective_standup_time || user?.standup_time || '09:00'
    const [eh, em] = eod.split(':').map(Number)
    const [sh, sm] = standup.split(':').map(Number)
    const now = new Date()
    const userNow = new Date(now.toLocaleString('en-US', { timeZone: user?.timezone || 'UTC' }))
    const nowMin = userNow.getHours() * 60 + userNow.getMinutes()
    const startMin = sh * 60 + sm
    const endMin = eh * 60 + em
    if (endMin > startMin) return nowMin < endMin
    return nowMin >= startMin || nowMin < endMin // overnight shift
  }

  const openCheckin = (wfh = false) => {
    setIsWfhCheckin(wfh)
    setShowCheckin(true)
  }
  const closeCheckin = () => { setShowCheckin(false); setIsWfhCheckin(false) }

  /** Main "Check Out" button — prompts for an early-leave reason first when
   * still inside the shift window. */
  const openCheckout = () => {
    // A session forgotten on an earlier day goes straight to the late check-out form.
    if (status?.stale_session_date) setShowCheckout(true)
    else if (isBeforeEod()) setShowEarlyLeavePrompt(true)
    else setShowCheckout(true)
  }
  /** Bypasses the early-leave gate — used when the checkout is already
   * explained by another action (e.g. informing a leave for today). */
  const forceOpenCheckout = () => setShowCheckout(true)
  const closeCheckout = () => { setShowCheckout(false); setPendingLeaveType(null); setEarlyLeaveReason('') }

  const cancelEarlyLeave = () => { setShowEarlyLeavePrompt(false); setEarlyLeaveReason('') }
  const confirmEarlyLeave = () => {
    if (!earlyLeaveReason.trim()) return
    setShowEarlyLeavePrompt(false)
    setShowCheckout(true)
  }

  const submitCheckin = (carryoverIds: string[], newGoals: string[], atTime?: string, reason?: string) => {
    checkinMutation.mutate({ carryover_goal_ids: carryoverIds, new_goals: newGoals, wfh: isWfhCheckin, at_time: atTime, reason })
  }
  const submitCheckout = (completedIds: string[], additional: string[], atTime?: string, reason?: string) => {
    if (checkoutData?.stale_session_date) {
      checkoutMutation.mutate({
        completed_goal_ids: completedIds,
        additional_completed: additional,
        at_time: atTime,
        late_reason: reason,
      })
      return
    }
    checkoutMutation.mutate({
      completed_goal_ids: completedIds,
      additional_completed: additional,
      early_leave_reason: reason || earlyLeaveReason || undefined,
      at_time: atTime,
    })
  }

  return {
    status, statusLoading, isCheckedIn, isCheckedOut,
    showCheckin, showCheckout, isWfhCheckin,
    checkinData, checkoutData,
    checkinMutation, checkoutMutation,
    openCheckin, closeCheckin, submitCheckin,
    openCheckout, forceOpenCheckout, closeCheckout, submitCheckout,
    showEarlyLeavePrompt, earlyLeaveReason, setEarlyLeaveReason, confirmEarlyLeave, cancelEarlyLeave,
    pendingMsg, clearPendingMsg: () => setPendingMsg(''),
    setPendingMsgFromServer: (msg: string) => setPendingMsg(msg),
    pendingLeaveType, setPendingLeaveType,
  }
}

export type UseCheckInOutResult = ReturnType<typeof useCheckInOut>
